import * as React from 'react';
import { Icon, mergeStyles } from '@fluentui/react';

export type Tone = 'success' | 'info' | 'warning' | 'neutral' | 'danger';

interface ToneStyle {
    bg: string;
    fg: string;
    border: string;
}

// Fluent-aligned palette mirroring the v9 Badge semantic tones (success, informative,
// warning, danger, subtle). Colors resolve from --db-pill-* CSS variables (theme.css)
// so the pills follow the light/dark theme instead of staying fixed light: light is a
// tint bg + saturated fg; dark flips to a dark tint bg + light fg. Both ramps meet WCAG
// AA (>=4.5:1) for 12px semibold text. CSS vars resolve fine inside mergeStyles.
const tones: Record<Tone, ToneStyle> = {
    success: { bg: 'var(--db-pill-success-bg)', fg: 'var(--db-pill-success-fg)', border: 'var(--db-pill-success-border)' },
    info:    { bg: 'var(--db-pill-info-bg)',    fg: 'var(--db-pill-info-fg)',    border: 'var(--db-pill-info-border)' },
    warning: { bg: 'var(--db-pill-warning-bg)', fg: 'var(--db-pill-warning-fg)', border: 'var(--db-pill-warning-border)' },
    danger:  { bg: 'var(--db-pill-danger-bg)',  fg: 'var(--db-pill-danger-fg)',  border: 'var(--db-pill-danger-border)' },
    neutral: { bg: 'var(--db-pill-neutral-bg)', fg: 'var(--db-pill-neutral-fg)', border: 'var(--db-pill-neutral-border)' },
};

const pillClass = (tone: Tone) => mergeStyles({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    backgroundColor: tones[tone].bg,
    color: tones[tone].fg,
    border: `1px solid ${tones[tone].border}`,
    whiteSpace: 'nowrap',
});

const iconClass = mergeStyles({ fontSize: 12 });

export interface PillProps {
    tone: Tone;
    iconName?: string;
    role?: string;
    ariaLabel?: string;
    children: React.ReactNode;
}

export const Pill: React.FC<PillProps> = ({ tone, iconName, role, ariaLabel, children }) => (
    <span className={pillClass(tone)} role={role} aria-label={ariaLabel}>
        {iconName && <Icon iconName={iconName} className={iconClass} aria-hidden="true" />}
        {children}
    </span>
);

export default Pill;
